import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useDispatch } from 'store';
import { clearBreadcrumbLeaf, setBreadcrumbLeaf } from 'store/reducers/breadcrumb';

/**
 * Identity of the dynamic tail crumb for the current URL, or `null` when the route
 * has no leaf slot. Derived purely from `location` so that both the writer
 * ({@link useBreadcrumbLeaf}, in the page) and the reader (`Breadcrumbs`, in the
 * header) compute the same key without sharing route config.
 *
 * A route owns a leaf iff it carries an entity-id param (`id`, `*Id`, or a `*Key` that names a record by a fixed
 * key, as `reportKey` does a report). Tab
 * params (`:tab`, `:invoiceTab`) are deliberately excluded so switching tabs on a
 * detail page keeps the same key — the leaf stays put instead of flickering. The
 * path up to the first id segment namespaces the key so same-id entities of
 * different types (`/matter/1` vs `/contact/1`, `/admin/job-roles/1` vs
 * `/admin/job-positions/1`) don't collide. A numeric segment counts as an id
 * too, so a reader that knows only the URL and the record's own `id` (the AI
 * panel) builds the same key as the page.
 */
export const getBreadcrumbLeafKey = (
    pathname: string,
    params: Readonly<Record<string, string | undefined>>
): string | null => {
    const idEntries = Object.entries(params).filter(
        ([key, value]) => !!value && (key === 'id' || key.endsWith('Id') || key.endsWith('Key'))
    );
    if (idEntries.length === 0) {
        return null;
    }
    const idValues = idEntries.map(([, value]) => value);
    const idValue = params.id ?? idValues.join('/');
    const segments = pathname.split('/').filter(Boolean);
    const idIndex = segments.findIndex((segment) => /^\d+$/.test(segment) || idValues.includes(segment));
    const namespace = segments.slice(0, Math.max(idIndex, 1)).join('/');
    return `${namespace}#${idValue}`;
};

/**
 * Publishes the current page's dynamic breadcrumb label into the store. The label
 * only fills the leaf slot whose presence the route structure already decided, so
 * its load lag delays text fill-in, never the enter/exit animation. No-ops for
 * routes without a leaf slot or with an empty label. `label` is the record's name; `crumb`, when given,
 * is what the crumb shows instead.
 */
export const useBreadcrumbLeaf = (label: string | undefined, crumb?: string): void => {
    const { pathname } = useLocation();
    const params = useParams();
    const dispatch = useDispatch();
    const leafKey = getBreadcrumbLeafKey(pathname, params);

    useEffect(() => {
        if (!leafKey || !label) {
            return;
        }
        dispatch(setBreadcrumbLeaf({ leafLabel: label, leafKey, leafCrumb: crumb }));
        return () => {
            // Clear immediately: Breadcrumbs snapshots the leaving crumb's label into its own
            // presence list and animates the exit from there (gated structurally by the URL),
            // so the store needn't linger. Guarded in the reducer so a sibling page that has
            // already claimed the slot is not wiped.
            dispatch(clearBreadcrumbLeaf({ leafKey, leafLabel: label }));
        };
    }, [dispatch, label, crumb, leafKey]);
};
